import React, { useState, useEffect, Fragment } from 'react';
import { useTheme, makeStyles } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';

import ListItem from '@material-ui/core/ListItem';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import ListItemText from '@material-ui/core/ListItemText';
import Avatar from '@material-ui/core/Avatar';

import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import Checkbox from '@material-ui/core/Checkbox';
import Alert from '@material-ui/lab/Alert';
import CardHeader from '@material-ui/core/CardHeader';
import RadioGroup from '@material-ui/core/RadioGroup';
import Radio from '@material-ui/core/Radio';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Menu from '@material-ui/core/Menu';

import AddIcon from '@material-ui/icons/Add';
import CloseIcon from '@material-ui/icons/Close';
import TicketIcon from '@material-ui/icons/ConfirmationNumber';
import UserIcon from '@material-ui/icons/Person';
import CompleteIcon from '@material-ui/icons/CheckCircle';

import Loading from 'app/components/loaders/Loading';
import UserSearchListRadio from 'app/components/user/UserSearchListRadio';

import states from 'app/utils/States';

import useApi from 'app/context/useApi';

const useStyles = makeStyles(theme => ({
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
    },
    ticketSelect: {
        paddingTop: theme.spacing(3),
        paddingBottom: theme.spacing(3),
    },
    successContainer: {
        paddingTop: theme.spacing(4),
        paddingBottom: theme.spacing(4),
        textAlign: 'center'
    },
    successIcon: {
        fontSize: '82px',
        color: theme.palette.success.main,
    },
    empty: {
        paddingTop: theme.spacing(4),
        paddingBottom: theme.spacing(4),
        backgroundColor: theme.palette.grey[200],
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
        textAlign: 'center'
    },
    userSearch: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2)
    },
    marginBottom: {
        marginBottom: theme.spacing(3)
    }
}));

const initialValues = {
    invitation_id: 0,
    ticket_id: 0,
    variant: null,
};

export default function InviteGuestDialog(props) {

    const { open, onClose, onClosed, onSave, invitation } = props;

    const classes = useStyles();
    const api = useApi();
    const theme = useTheme();

    const [ step, setStep ] = useState(1);
    const [ tickets, setTickets ] = useState([]);

    const [ values, setValues ] = useState(initialValues);
    const [ dates, setDates ] = useState([]);
    const [ ticket, setTicket ] = useState(null);

    const [ dateName, setDateName ] = useState("");

    const [ variants, setVariants ] = useState([]);
    const [ anchorEl, setAnchorEl] = useState(null);

    const [ loading, setLoading ] = useState(false);
    const [ errors, setErrors ] = useState({});

    // Entering Functions ------------------------------------------

    const handleEnter = () => {
        let s = 1;

        if (invitation) {
            setValues({ ...initialValues, invitation_id: invitation.id});
            setTickets(invitation.available_tickets);
        }

        setStep(s);
    }

    // Setter Functions ------------------------------------------

    const toggleTicket = (t, event) => {
        if (isError('ticket_id')) delete errors.ticket_id;

        if (isTicketChecked(t)) {
            setTicket(null);
            setValues({ ...values, variant: "" });
        } else {
            setTicket(t);

            if (t.variants.length) {
                openVariantDialog(t.variants, event.currentTarget);
            }
        }
    };

    const openVariantDialog = (vars, t) => {
        setVariants(vars);
        setAnchorEl(t);
    }

    const handleVariantChange = (variant) => {
        setValues({ ...values, variant: variant });
        handleCloseVariantDialog();
    } 

    const handleCloseVariantDialog = () => {
        setAnchorEl(null);
    }

    const handleDateName = e => {
        const value = e.target.value;

        setDateName(value);
    };

    const addDate = () => {
        if (dateName != '') {
            setDates([...dates, {name: dateName}]);
            setDateName('');
        }
    }

    const removeDate = i => {
        const newDates = [...dates];

        if (newDates[i]) newDates.splice(i,1);

        setDates(newDates);
    }

    const datesAllowed = ticket ? ticket.dates_allowed : invitation ? invitation.dates_allowed : 0;
    const datesLeft = (datesAllowed - dates.length);

    useEffect (() => {
        if (datesAllowed < dates.length) {
            const newDates = [...dates];
            newDates.splice(-1, (dates.length - datesAllowed));
            setDates(newDates);
        }
    }, [ticket]);

    // General Functions --------------------------------------------

    const handleNext = () => {

        switch (step) {
            case 1:
                if (ticket && ticket.dates_allowed > 0) setStep(2);
                else setStep(3);
                break;
            case 2:
                setStep(3);
                break;
            case 3:
                postTicket();
                break;
            case 4:
                onSave();
                break;

            default: break;
        }
    }

    const handleBack = () => {

        switch (step) {
            case 2:
                setStep(1);
                break;
            case 3:
                if (ticket && ticket.dates_allowed > 0) setStep(2);
                else setStep(1);
                break;
            default: break;
        }
    }

    useEffect(() => {
        const content = document.getElementById("invite-guest-dialog-content");

        if (content) {
            content.scrollTo(0, 0);
        }
    }, [step])

    const isNextDisabled = () => {
        if (step === 1 && !ticket) return true;
        if (step > 4) return true;
        return false;
    }

    const isBackDisabled = () => {
        if (step < 2) return true;
        if (step > 3) return true;
        return false;
    }

    const nextText = () => {
        if (step === 3) return "Confirm Ticket Selection";
        if (step === 4) return "Close";
        return "Next";
    }

    // Post functions ------------------------------------------

    const postTicket = () => {

        setLoading(true);

        const data = {
            ...values,
            dates: dates,
            ticket_id: ticket.id
        };

        api.post(`/ticket/new`, data)
            .then(() => setStep(4))
            .catch(error => {
                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors)
                }
            }).finally(() => setLoading(false));
    };

    // Error Handling -------------------------------------------

    const isError = field => {
        if (errors[field]) return true;
        return false;
    }

    const getErrorMessage = field => {
        if (isError(field)) return errors[field];
        return "";
    }

    // Exting Functions ----------------------------------------

    const clearState = () => {
        setValues({ ...initialValues });
        setDateName("");
        setDates([]);
        setTicket(null);
        setStep(1);
    };

    const handleClosed = () => {
        clearState();

        if (onClosed) onClosed();
    }

    // Constanrs

    const isTicketChecked = t => {
        if (ticket && ticket.id === t.id) return true;
        return false;
    }

    return (
        <Dialog
            aria-labelledby="select-ticket-dialog-title"
            open={open}
            aria-describedby="select-ticket-dialogue-description"
            fullWidth
            maxWidth="sm"
            disableBackdropClick
            fullScreen={useMediaQuery(theme.breakpoints.down('sm'))}
            TransitionProps={{
                onExited: handleClosed,
                onEnter: handleEnter
            }}
            >
            <Loading isLoading={loading}/>
            <DialogTitle id="invite-guest-dialog-title">
                Select A Ticket
                <IconButton aria-label="Cancel" className={classes.closeButton} onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers id="select-ticket-dialog-content">
                {step === 1 &&
                    <form>
                        {(invitation.event.group_tickets_required) && (
                            <Alert severity="warning" className={classes.marginBottom}>
                                A ticket is required for this event.
                            </Alert>
                        )}
                        <Typography variant="body1" color="textSecondary" className={classes.marginBottom}>
                            Please select the appropriate ticket below. You will be able to pay once your ticket has been reserved.
                        </Typography>
                        {(tickets && tickets.length > 0) ? tickets.map(t => (
                            <Paper key={t.id} className={classes.marginBottom} elevation={4}>
                                <ListItem ContainerComponent="div">
                                    <ListItemAvatar>
                                        <Avatar>
                                            <TicketIcon />
                                        </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={
                                            <Fragment>
                                                <Typography
                                                    variant="subtitle2"
                                                    color="textPrimary"
                                                    >
                                                    {t.title} {(values.variant && values.variant != "") ? "(" + values.variant + ")" : null}
                                                </Typography>
                                                <Typography
                                                    variant="subtitle2"
                                                    color="textSecondary"
                                                    gutterBottom
                                                    >
                                                    {t.description}
                                                </Typography>
                                            </Fragment>
                                        }
                                        secondary={
                                            <Typography
                                                component="div"
                                                variant="body2"
                                                color="textSecondary"
                                                >
                                                Price: <strong>${t.current_price}</strong>
                                            </Typography>
                                        }/>
                                    <ListItemSecondaryAction>
                                        <Checkbox color="secondary" checked={isTicketChecked(t)} onChange={(event) => toggleTicket(t, event)} />
                                    </ListItemSecondaryAction>
                                </ListItem>
                            </Paper>
                        )) : (
                            <Typography variant="body1" color="textSecondary" className={classes.empty}>
                                No tickets are available for this event at this time. Please try again later.
                            </Typography>
                        )}
                        <Menu
                            id={`variant-menu`}
                            anchorEl={anchorEl}
                            keepMounted
                            open={Boolean(anchorEl)}
                            onClose={handleCloseVariantDialog}
                            >
                            <MenuItem
                                key={0}
                                disabled
                                >
                                Please Select a Ticket Variant
                            </MenuItem>
                            {variants.map((option, i) => (
                                <MenuItem
                                    key={i + 1}
                                    onClick={() => handleVariantChange(option)}
                                    >
                                    {option}
                                </MenuItem>
                            ))}
                        </Menu>
                    </form>
                }
                {(step === 2) &&
                    <form>
                        <Typography variant="body1" color="textSecondary" className={classes.marginBottom}>
                            Add the dates of your guest to the invitation. The ticket you selected allows up to {datesAllowed} date(s).
                        </Typography>
                        {(dates.length > 0) && dates.map((d, i) => (
                            <Paper key={i} elevation={4} className={classes.marginBottom}>
                                <CardHeader
                                    avatar={
                                        <Avatar >
                                            <UserIcon />
                                        </Avatar>
                                    }
                                    title={d.name}
                                    action={
                                        <IconButton
                                            aria-label="Remove Date"
                                            onClick={() => removeDate(i)}
                                            >
                                            <CloseIcon />
                                        </IconButton>
                                    }
                                    className={classes.marginBottom}
                                />
                            </Paper>

                        ))}
                        <CardHeader
                            avatar={
                                <Avatar >
                                    <UserIcon />
                                </Avatar>
                            }
                            title={
                                <>
                                    <TextField
                                        id="date-name"
                                        label="Date Name"
                                        value={dateName}
                                        onChange={handleDateName}
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                    />
                                </>
                            }
                            subtitle="Enter date name and press the add button on the right to save."
                            action={
                                <IconButton
                                    aria-label="Remove Inviter"
                                    onClick={addDate}
                                    disabled={(datesLeft < 1)}
                                    >
                                    <AddIcon />
                                </IconButton>
                            }
                            className={classes.marginBottom}
                        />
                        <Typography variant="subtitle1" color="primary" gutterBottom>
                            <strong>{dates.length}</strong> date(s) added to invitation.
                        </Typography>
                    </form>
                }
                {step === 3 &&
                    <div>
                        <Typography variant="h6" color="textSecondary" className={classes.marginBottom}>
                            Please review your ticket selection...
                        </Typography>
                        <Paper className={classes.marginBottom} elevation={4}>
                            <ListItem ContainerComponent="div">
                                <ListItemAvatar>
                                    <Avatar>
                                        <TicketIcon />
                                    </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                    primary={
                                        <Fragment>
                                            <Typography
                                                variant="subtitle2"
                                                color="textPrimary"
                                                >
                                                {ticket.title} {(values.variant && values.variant != "") ? "(" + values.variant + ")" : null}
                                            </Typography>
                                            <Typography
                                                variant="subtitle2"
                                                color="textSecondary"
                                                gutterBottom
                                                >
                                                {ticket.description}
                                            </Typography>
                                        </Fragment>
                                    }
                                    secondary={
                                        <Typography
                                            component="div"
                                            variant="body2"
                                            color="textSecondary"
                                            >
                                            Price: <strong>${ticket.current_price}</strong>
                                        </Typography>
                                    }/>
                            </ListItem>
                        </Paper>
                        {(dates.length > 0) ? (
                            <Paper className={classes.marginBottom} elevation={4}>
                                You have added {dates.length} dates to your ticket.
                            </Paper>
                        ) : (dates.length === 0 && datesAllowed > 0) ? (
                            <Alert severity="warning" className={classes.marginBottom}>
                                You have not added any dates to your ticket. You can add dates now by hitting the "BACK" button or later on the event page.
                            </Alert>
                        ) : (
                            <></>
                        )}
                    </div>
                }
                {step === 4 &&
                    <div className={classes.successContainer}>
                        <div className={classes.successIcon}>
                            <CompleteIcon color="inherit" fontSize="inherit" />
                        </div>
                        <Typography variant="h5" color="textSecondary" gutterBottom>
                            Ticket Saved.
                        </Typography>
                        <Typography variant="body1" color="textSecondary" gutterBottom>
                            You can access your ticket at anytime on the event page as well as pay and edit dates.
                        </Typography>
                    </div>
                }
            </DialogContent>
            <DialogActions>
                <Button onClick={handleBack} color="default" disabled={isBackDisabled()}>
                    Back
                </Button>
                <Button onClick={handleNext} color="secondary" disabled={isNextDisabled()} variant="contained">
                    {nextText()}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
